import React, { useRef } from 'react';
import ReactModal from 'react-modal';
import PropTypes from 'prop-types';
import Icon from '@/components/ui/Icon';
import styles from './Modal.module.scss';

if (process.env.NODE_ENV !== 'test') ReactModal.setAppElement('#content_container, #main, #root');

let headerIdCounter = 0;

interface ModalProps {
  allowOverflow?: boolean;
  bodyClassName?: string;
  bodyId?: string;
  children: React.ReactNode;
  className?: string;
  closeButtonClassName?: string,
  closeModal?: () => void;
  closeable?: boolean;
  error?: boolean;
  errorMessage?: string;
  footer?: React.ReactNode;
  fullScreen?: boolean;
  headerClass?: string;
  headerText?: React.ReactNode;
  headerTextClassName?: string;
  id?: string;
  isLoading?: boolean;
  isOpen?: boolean;
  nearlyFullScreen?: boolean;
  overlayClassName?: string;
  parentSelector?: () => HTMLElement;
  portalClassName?: string;
  preventScroll?: boolean;
  reflow?: boolean;
  shouldCloseOnOverlayClick: boolean;
}

const spinner = () => (
  <div className="text--center">
    <i aria-label="Loading" className="fa fa-spinner fa-spin fa-2x" />
  </div>
);

const Modal = ({
  allowOverflow,
  bodyClassName,
  bodyId,
  children,
  className,
  closeButtonClassName,
  closeModal,
  closeable,
  error,
  errorMessage,
  footer,
  fullScreen,
  headerClass,
  headerText,
  headerTextClassName,
  id,
  isLoading,
  isOpen,
  nearlyFullScreen,
  overlayClassName,
  parentSelector,
  portalClassName,
  preventScroll,
  reflow,
  shouldCloseOnOverlayClick,
}: ModalProps) => {
  const headerIdRef = useRef<string | null>(null);
  if (headerIdRef.current === null) headerIdRef.current = `modal-header-${headerIdCounter += 1}`;
  const headerId = headerIdRef.current;
  const isCloseableOnClick = shouldCloseOnOverlayClick === undefined ? closeable : shouldCloseOnOverlayClick;

  const overflowClass = () => (allowOverflow ? styles.allowOverflow : '');

  const reflowClass = () => (reflow ? styles.reflow : '');

  const modalSizeClass = () => {
    if (fullScreen) return styles.fullScreen;
    if (nearlyFullScreen) return styles.nearlyFullScreen;

    return null;
  };

  const closeButton = () => (
    <button
      aria-label="Close popup"
      className={`${styles.closeBtn} ${closeButtonClassName} close tw-h-[30px]`}
      type="button"
      onClick={closeModal}
      onKeyPress={closeModal}
    >
      <Icon icon="Close" size={20} />
    </button>
  );

  const portalClass = () => {
    if (portalClassName) return `${styles.modalPortal} ${portalClassName}`;

    return styles.modalPortal;
  };

  const modalContent = () => {
    if (error) {
      return <div className={styles.error}>{errorMessage}</div>;
    }

    return (
      <div
        className={`${styles.body} ${bodyClassName} ${overflowClass()} ${reflowClass()} ${modalSizeClass()}`}
        id={bodyId}
      >
        {isLoading ? Modal.spinner() : children}
      </div>
    );
  };

  return (
    <ReactModal
      aria={{ hidden: !isOpen, labelledby: headerId, modal: true }}
      className={`${styles.modalContainer} ${className} ${overflowClass()} ${reflowClass()} ${modalSizeClass()}`}
      contentRef={(node) => { if (node && id) node.setAttribute('id', id); }}
      // AIDEV-NOTE: react-modal hardcodes aria-modal on the roleless overlay div, where ARIA
      // prohibits it. Strip it there; the `aria` prop below puts it on the role="dialog" content.
      overlayRef={(node) => { node?.removeAttribute('aria-modal'); }}
      isOpen={isOpen}
      overlayClassName={`${styles.overlayBackground} ${overlayClassName}`}
      parentSelector={parentSelector}
      portalClassName={portalClass()}
      preventScroll={preventScroll}
      role="dialog"
      shouldCloseOnOverlayClick={isCloseableOnClick}
      onRequestClose={closeModal}
    >
      <header className={`${styles.header} ${headerClass}`}>
        {closeable && closeButton()}
        <h2 className={`pb0 ${headerTextClassName}`} id={headerId}>{headerText}</h2>
      </header>
      {modalContent()}
      {footer && footer}
    </ReactModal>
  );
};

Modal.propTypes = {
  allowOverflow: PropTypes.bool,
  bodyClassName: PropTypes.string,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  closeButtonClassName: PropTypes.string,
  closeModal: PropTypes.func,
  closeable: PropTypes.bool,
  error: PropTypes.bool,
  errorMessage: PropTypes.string,
  footer: PropTypes.node,
  fullScreen: PropTypes.bool,
  headerClass: PropTypes.string,
  headerText: PropTypes.node,
  headerTextClassName: PropTypes.string,
  id: PropTypes.string,
  isLoading: PropTypes.bool,
  isOpen: PropTypes.bool,
  nearlyFullScreen: PropTypes.bool,
  overlayClassName: PropTypes.string,
  parentSelector: PropTypes.func,
  portalClassName: PropTypes.string,
  preventScroll: PropTypes.bool,
  reflow: PropTypes.bool,
  shouldCloseOnOverlayClick: PropTypes.bool,
};

Modal.defaultProps = {
  allowOverflow: false,
  bodyClassName: '',
  className: 'new-modal-container',
  closeButtonClassName: '',
  closeModal: () => { },
  closeable: true,
  error: false,
  errorMessage: 'There was an error. Please reload the page and try again.',
  footer: null,
  fullScreen: false,
  headerClass: '',
  headerText: 'Header Text',
  headerTextClassName: '',
  id: undefined,
  isLoading: false,
  isOpen: false,
  nearlyFullScreen: false,
  overlayClassName: '',
  parentSelector: () => document.body,
  portalClassName: null,
  preventScroll: false,
  reflow: false,
  shouldCloseOnOverlayClick: undefined,
};

Modal.spinner = spinner;

export default Modal;
